import React from 'react';

export interface ToastMessage {
  id: number;
  text: string;
  type: 'success' | 'error';
}

interface TerminologyToastProps {
  messages: ToastMessage[];
  onDismiss: (id: number) => void;
}

export function TerminologyToast({ messages, onDismiss }: TerminologyToastProps) {
  if (messages.length === 0) return null;
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: 'fixed',
        bottom: 'var(--spacing-6)',
        right: 'var(--spacing-6)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-2)',
        zIndex: 2000,
      }}
    >
      {messages.map((msg) => (
        <div
          key={msg.id}
          role="status"
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderRadius: 'var(--radius-md)',
            background: msg.type === 'success' ? 'var(--color-success)' : 'var(--color-danger)',
            color: 'var(--color-text-inverse)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            boxShadow: 'var(--shadow-md)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            maxWidth: 360,
            cursor: 'pointer',
          }}
          onClick={() => onDismiss(msg.id)}
        >
          <span>{msg.text}</span>
          <span style={{ marginLeft: 'auto', opacity: 0.7, fontSize: 'var(--font-size-xs)' }}>
            ✕
          </span>
        </div>
      ))}
    </div>
  );
}

// Toast state management hook
let _toastIdCounter = 0;

export function useToast() {
  const [messages, setMessages] = React.useState<ToastMessage[]>([]);

  const addToast = React.useCallback((text: string, type: 'success' | 'error' = 'success') => {
    const id = ++_toastIdCounter;
    setMessages((prev) => [...prev, { id, text, type }]);
    setTimeout(() => setMessages((prev) => prev.filter((m) => m.id !== id)), 4000);
  }, []);

  const dismissToast = React.useCallback((id: number) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  return { messages, addToast, dismissToast };
}
